import React, { FC } from 'react';
import { RockyModal } from '@shared/components/RockyModal';
import { Stack, Typography, Button, Box, Divider } from '@mui/material';
import { makeStyles } from '@mui/styles';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import LightbulbOutlinedIcon from '@mui/icons-material/LightbulbOutlined';
import CheckCircleOutlinedIcon from '@mui/icons-material/CheckCircleOutlined';

const useStyles = makeStyles({
  modal: {
    maxWidth: '480px',
    width: '100%',
  },
  modalHeader: {
    padding: '20px 30px',
  },
  modalBody: {
    padding: '24px 30px',
  },
  infoBlock: {
    display: 'flex',
    gap: '12px',
    padding: '16px',
    backgroundColor: '#F5F7FA',
    borderRadius: '8px',
    marginBottom: '8px',
  },
  infoIcon: {
    color: '#1976d2',
    marginTop: '2px',
    flexShrink: 0,
  },
  sectionBlock: {
    display: 'flex',
    gap: '12px',
    padding: '12px 0',
  },
  sectionIcon: {
    color: '#666',
    marginTop: '2px',
    flexShrink: 0,
    fontSize: '20px',
  },
  sectionContent: {
    flex: 1,
  },
  sectionTitle: {
    fontWeight: 600,
    marginBottom: '4px',
    color: '#333',
  },
  highlightText: {
    color: '#1976d2',
    fontWeight: 600,
  },
});

interface Props {
  isOpen: boolean;
  handleClose: () => void;
  userName: string;
  evaluationStatus: string;
}

export const EvaluationStatusWarningModal: FC<Props> = ({ handleClose, isOpen, userName, evaluationStatus }) => {
  const styles = useStyles();

  return (
    <RockyModal
      crossIcon
      title="A note on evaluation status"
      isOpen={isOpen}
      onClose={handleClose}
      backdropClose={true}
      modalStyles={styles.modal}
      modalHeaderStyles={styles.modalHeader}
      modalBodyStyles={styles.modalBody}
    >
      <Stack spacing={2}>
        <Box className={styles.infoBlock}>
          <InfoOutlinedIcon className={styles.infoIcon} />
          <Typography variant="body1" sx={{ lineHeight: 1.6 }}>
            Before you proceed, please be aware that {userName} has "{evaluationStatus}" evaluation.
          </Typography>
        </Box>

        <Divider sx={{ my: 1 }} />

        <Box className={styles.sectionBlock}>
          <LightbulbOutlinedIcon className={styles.sectionIcon} />
          <Box className={styles.sectionContent}>
            <Typography variant="body2" className={styles.sectionTitle}>
              Reason
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
              To ensure they receive timely feedback, the "{evaluationStatus}" evaluation should be completed before the
              last day on the project.
            </Typography>
          </Box>
        </Box>

        <Box className={styles.sectionBlock}>
          <CheckCircleOutlinedIcon className={styles.sectionIcon} />
          <Box className={styles.sectionContent}>
            <Typography variant="body2" className={styles.sectionTitle}>
              Next step
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
              You can still submit the NewHome and "Reporting to" transfer request now.
            </Typography>
          </Box>
        </Box>

        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
          <Button variant="contained" onClick={handleClose} size="medium">
            Got it
          </Button>
        </Box>
      </Stack>
    </RockyModal>
  );
};
